import { Images, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import type { TourListItem } from "../../shared/api-types";
import { useT } from "../i18n";

/**
 * Drei Zustände, eine Frage: „Sieht die Welt draußen den aktuellen Stand?“
 * Entwurf (grau) · Veröffentlicht (grün) · Veröffentlicht mit offenen Änderungen (amber).
 * Das Schloss steht dazu, wenn die öffentliche Tour ein Passwort verlangt.
 */
export function StatusBadge({
  status,
  hasChanges = false,
  locked = false,
}: {
  status: "draft" | "published";
  hasChanges?: boolean;
  locked?: boolean;
}) {
  const t = useT();
  const published = status === "published";
  const tone = !published
    ? "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
    : hasChanges
      ? "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400"
      : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400";
  const label = !published
    ? t.dashboard.statusDraft
    : hasChanges
      ? t.dashboard.statusPublishedChanges
      : t.dashboard.statusPublished;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {published && locked && (
        <span title={t.dashboard.statusLocked} className="inline-flex">
          <Lock className="size-3" aria-label={t.dashboard.statusLocked} />
        </span>
      )}
      {label}
    </span>
  );
}

/** Farbe des Statuspunkts in der Seitenleiste — dieselbe Ampel wie im Abzeichen. */
export function statusDotClass(tour: TourListItem): string {
  if (tour.status !== "published") return "bg-zinc-400 dark:bg-zinc-600";
  return tour.hasChanges ? "bg-amber-500" : "bg-emerald-500";
}

/** Klickbare Tourkachel mit Vorschaubild — Übersicht und Projektseite teilen sie sich. */
export function TourCard({ tour, eyebrow }: { tour: TourListItem; eyebrow?: string }) {
  const t = useT();
  return (
    <Link
      to={`/tours/${tour.id}`}
      className="group overflow-hidden rounded-2xl border border-zinc-200 bg-white transition-shadow hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-zinc-700"
    >
      <div className="aspect-[2/1] overflow-hidden bg-zinc-100 dark:bg-zinc-800">
        {tour.posterUrl ? (
          <img
            src={tour.posterUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="grid h-full place-items-center">
            <Images className="size-7 text-zinc-300 dark:text-zinc-600" />
          </div>
        )}
      </div>
      <div className="p-4">
        {eyebrow && (
          <p className="mb-0.5 truncate text-xs text-zinc-400 dark:text-zinc-500">{eyebrow}</p>
        )}
        <div className="flex items-start justify-between gap-3">
          <h3 className="truncate font-medium">{tour.name}</h3>
          <StatusBadge
            status={tour.status}
            hasChanges={tour.hasChanges}
            locked={tour.passwordProtected}
          />
        </div>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          {tour.sceneCount === 0
            ? t.dashboard.noScenes
            : t.dashboard.sceneCount(tour.sceneCount) +
              (tour.readyCount < tour.sceneCount ? t.dashboard.readySuffix(tour.readyCount) : "")}
        </p>
      </div>
    </Link>
  );
}
